import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HttpError } from '../Services/common';

export type StatusState = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';
export type ErrorType =
	| 'NOT_FOUND'
	| 'FORBIDDEN'
	| 'UNAUTHORIZED'
	| 'BAD_REQUEST'
	| 'CONFLICT'
	| 'SERVER_ERROR'
	| 'NETWORK_ERROR'
	| 'UNKNOWN';

export function useBizQuery<TQueryFnData, TError, TQueryKey extends readonly unknown[]>(
	queryKey: TQueryKey,
	queryFn: () => Promise<TQueryFnData>,
	enabled?: boolean,
	staleTimeSeconds?: number,
	refetchOnMount?: boolean | 'always',
	keepPreviousData?: boolean
) {
	const { t } = useTranslation();
	const fallbackMessage = t('REQUEST_FAILED', {
		defaultValue: 'The request could not be completed. Please try again. If the problem persists, contact support.',
	});
	const [statusState, setStatusState] = useState<StatusState>('INITIAL');
	const [errorType, setErrorType] = useState<ErrorType | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const queryOptions: UseQueryOptions<TQueryFnData, TError, TQueryFnData> = {
		queryKey,
		queryFn,
		enabled,
		refetchOnMount: refetchOnMount,
	};

	if (staleTimeSeconds !== undefined) {
		queryOptions.staleTime = staleTimeSeconds * 1000;
	}

	if (keepPreviousData) {
		queryOptions.placeholderData = (previousData) => previousData;
	}

	const { data, error, isLoading, isFetching, isSuccess, isError, isPlaceholderData, refetch } = useQuery<
		TQueryFnData,
		TError,
		TQueryFnData
	>(queryOptions);

	useEffect(() => {
		if (isLoading) {
			setStatusState('LOADING');
			setErrorMessage(null);
		} else if (isError) {
			setStatusState('ERROR');
			setErrorType(getErrorType(error));
			setErrorMessage(getErrorMessage(error, fallbackMessage));
		} else if (isSuccess) {
			setStatusState('SUCCESS');
			setErrorMessage(null);
		}
	}, [isLoading, isError, isSuccess]);

	return { data, error, errorType, errorMessage, statusState, isFetching, isPlaceholderData, refetch };
}

export function useBizMutation<TData, TError, TParams, TContext, TQueryKey extends readonly unknown[]>(
	mutationFn: (params: TParams) => Promise<TData>,
	options?: {
		getQueryKeyForInvalidation?: (variables: TParams) => TQueryKey[];
		onSuccessFn?: (result: TData) => void;
	}
) {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const fallbackMessage = t('REQUEST_FAILED', {
		defaultValue: 'The request could not be completed. Please try again. If the problem persists, contact support.',
	});
	const [statusState, setStatusState] = useState<StatusState>('INITIAL');
	const [errorType, setErrorType] = useState<ErrorType | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const mutation = useMutation<TData, TError, TParams, TContext>({
		mutationFn,
		onMutate: async () => {
			setStatusState('LOADING');
			setErrorMessage(null);
			return undefined;
		},
		onSuccess: (result, variables) => {
			setStatusState('SUCCESS');
			if (options?.onSuccessFn) {
				options.onSuccessFn(result);
			}
			if (options?.getQueryKeyForInvalidation) {
				const queryKeys = options.getQueryKeyForInvalidation(variables);
				queryKeys.forEach((keys) => {
					queryClient.invalidateQueries({ queryKey: keys });
				});
			}
		},
		onError: (error) => {
			setStatusState('ERROR');
			setErrorType(getErrorType(error));
			setErrorMessage(getErrorMessage(error, fallbackMessage));
		},
		onSettled: () => {
			const timeout = setTimeout(() => setStatusState('INITIAL'), 3000);
			return () => {
				clearTimeout(timeout);
			};
		},
	});

	// Memoize the mutate function to prevent unnecessary re-renders
	const stableMutate = useCallback((params: TParams) => mutation.mutate(params), [mutation.mutate]);

	// Return a stable object structure
	return {
		mutate: stableMutate,
		mutateAsync: mutation.mutateAsync,
		data: mutation.data,
		error: mutation.error,
		isError: mutation.isError,
		isIdle: mutation.isIdle,
		isPending: mutation.isPending,
		isSuccess: mutation.isSuccess,
		variables: mutation.variables,
		reset: mutation.reset,
		statusState,
		errorType,
		errorMessage,
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getErrorType(error: any): ErrorType {
	let status;
	if (error instanceof HttpError) {
		console.log('HttpError detected with status:', error.status);
		status = error.status;
	}

	if (!status) {
		status = error?.response?.status || error?.status;
	}

	if (!status) {
		return 'NETWORK_ERROR';
	}

	switch (status) {
		case 400:
			return 'BAD_REQUEST';
		case 401:
			return 'UNAUTHORIZED';
		case 403:
			return 'FORBIDDEN';
		case 404:
			return 'NOT_FOUND';
		case 409:
			return 'CONFLICT';
		case 500:
		case 502:
		case 503:
		case 504:
			return 'SERVER_ERROR';
		default:
			return 'UNKNOWN';
	}
}

function getErrorProperty(value: unknown, key: string): unknown {
	return typeof value === 'object' && value !== null && key in value ? (value as Record<string, unknown>)[key] : undefined;
}

function getResponseMessage(value: unknown): string | null {
	for (const key of ['message', 'Message', 'internalMessage']) {
		const message = getErrorProperty(value, key);
		if (typeof message === 'string' && message.length > 0) return message;
	}
	return null;
}

function getErrorMessage(error: unknown, fallbackMessage: string): string | null {
	if (!error) {
		return null;
	}

	if (error instanceof HttpError) {
		if (error.body) {
			try {
				const parsedBody = JSON.parse(error.body);
				return (
					getResponseMessage(parsedBody) ||
					(typeof parsedBody === 'string' && parsedBody.trim() ? parsedBody : fallbackMessage)
				);
			} catch {
				// IIS/proxies may return an HTML error page rather than an API message.
				return error.body.trim() && !/<[^>]+>/.test(error.body) ? error.body : fallbackMessage;
			}
		}

		return fallbackMessage;
	}

	const responseMessage = getResponseMessage(getErrorProperty(getErrorProperty(error, 'response'), 'data'));
	if (responseMessage) {
		return responseMessage;
	}

	const message = getErrorProperty(error, 'message');
	if (typeof message === 'string' && message.length > 0) {
		return message;
	}

	return null;
}
